/**
 * A-01 注文する（統合カタログ）＝FR-43・FR-14・FR-126（D-83 → D-95 → **M1.9 の D-99**）。
 *
 * **写真つきのカタログで選ぶだけ**（LP の約束）＝自由文で書かせない（D-39）。
 * 出るのは**取引単価**（単価3層の解決値）。掛率で解かれた商品だけは**定価と掛率も返る**
 * （D-101・M1.9b）＝シートで「なぜこの値段か」を3行で見せられる。
 *
 * ★**M1.8（D-95）＝商品起点のフラット一覧**。並びは**分類→品名→卸（接続順）**で決定的・
 *   分類チップは `products.category` 由来・カタログ名は職人に見せない（この骨格は M1.9 でも不変）。
 * ★**M1.9（D-99）＝2列カード＋商品詳細シート**（Design order-app.js の EC 型）：
 *   - カードは**写真を上に置いた2列グリッド**（スマホ）。写真・品名・価格が主役で、
 *     数量の操作はカードから消える＝一覧が「選ぶ」に専念する。
 *   - **数量→カートに入れる、は商品詳細シート**（カードのタップで開く）。シートには
 *     **「同じ品名のお取扱い（参考）」**＝同名・同規格の商品を**表示中のものも含めて**並べ、
 *     タップで表示を切り替えられる＝Design の「発注先を選ぶ」の狙い（比べて選ぶ）を、
 *     **名寄せせずに**回収する（卸が違えば別商品＝FR-126 の原則は破らない。だから「参考」）。
 *   - 最短お届け＝便マスタ（bins API）の `earliest_date`／締め時刻から出す。
 * ★**M1.9b＝Design（order-app.js/css）への忠実化**：
 *   - カタログ＝使い方ガイド帯（`.oa-guide`・×で `localStorage` に永続）／虫めがね内蔵の
 *     検索箱（`.oa-search`）／カードは Design の並び（写真→品名→規格→**卸名は素のテキスト**
 *     →価格）。**ロット表記はカードから外した**＝一覧は「選ぶ」に専念し、買い方の条件
 *     （◯◯から・◯◯単位）は決める場所＝シートで言う。
 *   - シートの卸行は Design の `.sup-opt`＝**表示中のものを含む**ラジオ型の並び（締め・便と
 *     単価を各行に出し、値段に差があるとき最安に印）。
 *   - シートの価格は Design の `.pd-price`＝**定価（打ち消し）→掛け率→取引単価**の3行。
 *     掛率で解かれていない商品（個別単価・定価そのまま）は**取引単価の1行だけ**。
 *   - **在庫は出さない**（`.pd-avail` は liff.css で DESIGN-HOLD＝在庫データが無い）。
 * ★**商品の名寄せはしない**（FR-126・D-17）＝同じような商品が卸ごとに別カードで並ぶのが正。
 * ★**停止中の卸はカタログから外す**（03 §8）＝黙って消さず、外した理由をその場に出す。
 * ★**PC（≥1024px）は右固定カート**（D-98⑤・FR-123 改）＝グリッドの隣に `CartRail`。
 *   確定は従来どおり /checkout（レールから直接は確定させない＝氏名ゲート・冪等キーがある）。
 */

import type { ConnectedTenant, PartnerBin, PartnerProduct, partnerApi } from "@kenzai/api-client";
import { Card, Chip, Empty, ErrorNote, Money, Skeleton, formatDate } from "@kenzai/ui";
import { useQueries } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useCart } from "../cart";
import { Sheet } from "../Sheet";
import { useIsDesktop } from "../useIsDesktop";
import { CartRail } from "./Cart";
import { Stepper } from "./formKit";

type Client = ReturnType<typeof partnerApi>;

/**
 * 分類が未設定の商品の畳み先（D-95③）。**並びでは必ず最後**。
 */
const OTHER = "その他";

/** 品名・分類の並び替え（ja 照合）。`localeCompare` を毎回作らないよう1つを使い回す。 */
const COLLATOR = new Intl.Collator("ja");

/** 検索の突き合わせ用（大小・全角空白・前後空白のゆらぎを吸収する。判定はここだけ）。 */
function normalize(s: string): string {
  return s.replace(/\u3000/g, " ").trim().toLowerCase();
}

/**
 * 検索の突き合わせ＝品名・規格・**商品番号**（M1.9b）。
 * ★番号を入れたのは、案内している探し方（placeholder「商品名・品番で検索」）と挙動を
 *   一致させるため。伝票の品番を書き写して探す使い方は現場で普通に起きる。
 */
function matches(p: PartnerProduct, q: string): boolean {
  if (q === "") return true;
  return normalize(`${p.name} ${p.spec ?? ""} ${p.display_no ?? ""}`).includes(q);
}

/**
 * 画面に並べる1枚のカード＝**商品 × 卸**（名寄せはしない＝FR-126・D-17）。
 */
type Entry = {
  key: string;
  product: PartnerProduct;
  tenant: ConnectedTenant;
  order: number;
  category: string;
};

/**
 * **見分けが付かないカードの鍵**＝`卸 × 品名`（D-95・T-D79 の申し送り＝一覧では
 * 見分けが要るカードにだけ商品番号を出す。詳細シートでは常に出す＝そこは「確かめる場所」）。
 */
function dupKey(e: Entry): string {
  return `${e.tenant.tenant_id}::${e.product.name}`;
}

/** 「◯◯から・◯◯単位」＝建材はロットで買う（qty=1 前提の EC 型にしない・05 §5.3）。 */
function lotText(p: PartnerProduct): string {
  const from = `${p.qty_start}${p.unit}から`;
  return p.qty_step > 1 ? `${from}・${p.qty_step}${p.unit}単位` : from;
}

/** 「同じ品名のお取扱い」の突き合わせ鍵＝品名＋規格の正規化一致（名寄せではなく参考）。 */
function sameProductKey(p: PartnerProduct): string {
  return `${normalize(p.name)}::${normalize(p.spec ?? "")}`;
}

/** 締め時刻の表示（API は `time` 型＝`"10:00:00"` で返る。画面に秒は要らない）。 */
function hhmm(t: string): string {
  return t.slice(0, 5);
}

/**
 * 便と締めの要旨（シートの卸行）。**締めが全便で同じなら Design と同じ1つの言い方**
 * （`10:00締め・午前便／午後便`）、便ごとに違うなら**便ごとに書く**
 * （`午前便 10:00締め／午後便 15:00締め`）＝1つの締めを代表させると、
 * 別の締めの便が同じ時刻だと誤解される。
 */
function binSummary(bins: PartnerBin[] | undefined): string {
  if (!bins || bins.length === 0) return "";
  const cutoffs = [...new Set(bins.map((b) => hhmm(b.cutoff_time)))];
  if (cutoffs.length === 1) return `${cutoffs[0]}締め・${bins.map((b) => b.name).join("／")}`;
  return bins.map((b) => `${b.name} ${hhmm(b.cutoff_time)}締め`).join("／");
}

/** 使い方ガイドを閉じた印（Design と同じキー＝端末に残る。消えたら次に開いたときまた出る）。 */
const GUIDE_KEY = "kz.oaGuideHidden";

function readGuideHidden(): boolean {
  try {
    return window.localStorage.getItem(GUIDE_KEY) === "1";
  } catch {
    // プライベートブラウズ等で localStorage を読めない端末では**毎回出す**（消せないより良い）
    return false;
  }
}

/**
 * `<button>` の素の見た目を消す（Design の `order-app.css` 冒頭にある
 * `button { font: inherit; color: inherit; background: none; border: none; cursor: pointer }` に相当）。
 * ★**この地の指定が liff.css に無い**ので、Design のクラスを載せた button にはここで足す。
 *   liff.css に同等の地が入ったら、この定数ごと外してよい。
 */
const BTN_RESET = { font: "inherit", color: "inherit", cursor: "pointer" } as const;

/** 1行で切る（卸名・便の要旨＝はみ出しても2行にしない）。 */
const ELLIPSIS = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;

function SearchIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.5" y2="16.5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

/** しおり＝「保存済みセット」への合図（Design の page-head 右端のボタン）。 */
function BookmarkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d="M18.5 21l-6.5-4.8L5.5 21V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function CheckIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="4 12 9 17 20 6" />
    </svg>
  );
}

export function Catalog({ client, connections }: { client: Client; connections: ConnectedTenant[] }) {
  // 停止中は API が 403 を返す（03 §8）＝引きに行かず、外した旨だけ出す。
  const usable = connections.filter((c) => c.tenant_status !== "suspended");
  const suspended = connections.filter((c) => c.tenant_status === "suspended");
  const isDesktop = useIsDesktop();

  const queries = useQueries({
    queries: usable.map((c) => ({
      queryKey: ["catalog", c.tenant_id],
      queryFn: () => client.catalog(c.tenant_id),
    })),
  });
  // 便マスタ＝**詳細シートの「最短お届け」用**（queryKey はカート・チェックアウトと共有＝二重取得しない）
  const binQueries = useQueries({
    queries: usable.map((c) => ({
      queryKey: ["bins", c.tenant_id],
      queryFn: () => client.bins(c.tenant_id),
    })),
  });
  const binsByTenant = useMemo(() => {
    const m = new Map<number, PartnerBin[]>();
    usable.forEach((c, i) => {
      const data = binQueries[i]?.data;
      if (data) m.set(c.tenant_id, data);
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useQueries の配列は毎回新しい参照
  }, [connections, binQueries.map((x) => x.dataUpdatedAt).join()]);

  /** 選択中の分類（null＝すべて）。**卸の絞り込みは無い**（D-96④・M1.9 でも維持＝D-98⑧）。 */
  const [category, setCategory] = useState<string | null>(null);
  // ★検索は URL の `?q=` と同期（D-98⑤）＝PC ヘッダーの横断検索がここに着地する。
  //   カタログ内の検索欄はローカル state（URL 側が変わったら追随する・打つたびに URL は汚さない）。
  const [params] = useSearchParams();
  const urlQ = params.get("q") ?? "";
  const [query, setQuery] = useState(urlQ);
  useEffect(() => {
    setQuery(urlQ);
  }, [urlQ]);
  const q = normalize(query);

  /** 開いている商品詳細シート（D-99）。 */
  const [sheetKey, setSheetKey] = useState<string | null>(null);
  /** カートに入れた直後の知らせ（シートは閉じるので、その場のトーストで返す）。 */
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (toast === null) return;
    const t = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(t);
  }, [toast]);

  /** 使い方ガイド（M1.9b・Design `.oa-guide`）＝×を押したら二度と出さない。 */
  const [guideHidden, setGuideHidden] = useState(readGuideHidden);
  function hideGuide() {
    setGuideHidden(true);
    try {
      window.localStorage.setItem(GUIDE_KEY, "1");
    } catch {
      // 保存できない端末でも、いま閉じたことは効かせる（この場は消える）
    }
  }

  const loading = queries.some((x) => x.isLoading);
  const failed = queries.find((x) => x.isError);

  // 卸ごとのカタログ → **商品の平坦な配列**（D-95①）。
  const entries = useMemo<Entry[]>(() => {
    const out: Entry[] = [];
    usable.forEach((tenant, i) => {
      // ★同じ商品が複数のカタログに載っていたら qty_start が小さい方を採る（D-95・M1.8 の決定）
      const best = new Map<number, PartnerProduct>();
      for (const c of queries[i]?.data ?? []) {
        for (const p of c.items) {
          const prev = best.get(p.product_id);
          if (!prev || p.qty_start < prev.qty_start) best.set(p.product_id, p);
        }
      }
      for (const p of best.values()) {
        out.push({
          key: `${tenant.tenant_id}:${p.product_id}`,
          product: p,
          tenant,
          order: i,
          category: p.category ?? OTHER,
        });
      }
    });

    // **分類 → 品名 → 卸（接続順）**。この順序が「同種商品が隣に並ぶ」の全部（D-95①）。
    out.sort((a, b) => {
      if (a.category !== b.category) {
        if (a.category === OTHER) return 1;
        if (b.category === OTHER) return -1;
        return COLLATOR.compare(a.category, b.category);
      }
      const byName = COLLATOR.compare(a.product.name, b.product.name);
      return byName !== 0 ? byName : a.order - b.order;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useQueries の配列は毎回新しい参照
  }, [connections, queries.map((x) => x.dataUpdatedAt).join()]);

  const categories = useMemo(() => {
    const seen: string[] = [];
    for (const e of entries) if (!seen.includes(e.category)) seen.push(e.category);
    return seen;
  }, [entries]);

  const ambiguous = useMemo(() => {
    const count = new Map<string, number>();
    for (const e of entries) count.set(dupKey(e), (count.get(dupKey(e)) ?? 0) + 1);
    return new Set([...count].filter(([, n]) => n >= 2).map(([k]) => k));
  }, [entries]);

  /**
   * 同じ品名・規格を扱う卸の数（M1.9b・PC のカードに「他N社」と出す＝Design「発注先：◯◯ 他N社」）。
   * ★**名寄せではない**（FR-126）＝カードは商品×卸のまま。「ほかにも扱いがある」と言うだけで、
   *   比べる場所は商品詳細シートの「同じ品名のお取扱い（参考）」。
   * ★数えるのは**卸**（同じ卸の別ロットは「他社」ではない）。
   */
  const altVendors = useMemo(() => {
    const m = new Map<string, Set<number>>();
    for (const e of entries) {
      const k = sameProductKey(e.product);
      let s = m.get(k);
      if (!s) {
        s = new Set();
        m.set(k, s);
      }
      s.add(e.tenant.tenant_id);
    }
    return m;
  }, [entries]);

  // ★**全クエリが揃うまで待つ構造は変えない**（部分表示は機能変更）。
  //   ロード中に `data-testid="catalog"` や商品由来の testid を出さない（E2E の前提）。
  if (loading) return <CatalogSkeleton />;

  const showVendorName = connections.length > 1;
  const showChips = categories.length > 1;
  const activeCategory = category !== null && categories.includes(category) ? category : null;

  const visible = entries.filter(
    (e) => (activeCategory === null || e.category === activeCategory) && matches(e.product, q),
  );
  const withHeadings = activeCategory === null && showChips;
  const sheetEntry = sheetKey === null ? null : (entries.find((e) => e.key === sheetKey) ?? null);

  const main = (
    <div style={{ display: "grid", gap: "var(--kz-space-3)", minWidth: 0 }} data-testid="catalog">
      {/* ★PC だけの見出し（Design `.page-head`）＝スマホは見出し無し（D-98③＝検索窓とタブの
          アクティブ表示が代わりをする）。**`<section>` を使わない**＝この画面の `section` は
          「分類の塊」の意味で E2E が数えている（multivendor.spec の並び順検査）。
          語は**タブと同じ「注文する」**（Design 語「発注する」に変えるとリッチメニューと
          押した言葉が食い違う＝D-98③）。 */}
      {isDesktop && (
        <div className="kp-head">
          <h1>注文する</h1>
          <p>接続中のすべてのお取引先の商品をまとめて表示しています（価格はお取引単価・税抜）。</p>
          <Link to="/sets" className="kp-head-act">
            <BookmarkIcon />
            保存済みセットから選ぶ
          </Link>
        </div>
      )}

      {/* 1社でも失敗したら黙らせない（他の卸は表示を続ける＝1社の障害で全部を止めない） */}
      {failed && <ErrorNote error={failed.error} />}

      {suspended.map((c) => (
        <p
          key={c.tenant_id}
          data-testid={`suspended-${c.tenant_id}`}
          style={{
            margin: 0,
            background: "var(--kz-status-danger-weak)",
            color: "var(--kz-status-danger)",
            border: "1px solid var(--kz-status-danger-border)",
            padding: "var(--kz-space-3) var(--kz-space-4)",
            borderRadius: "var(--kz-radius-md)",
            fontSize: "var(--kz-font-size-sm)",
            lineHeight: 1.6,
          }}
        >
          {c.tenant_name} は現在ご利用いただけません。お取引先にお問い合わせください。
        </p>
      ))}

      {/* 使い方ガイド（Design `.oa-guide`）＝**はじめて開いた人にだけ**3手を言う。
          ★margin は CSS が持っているが、この器は grid の gap で間隔を作っているので殺す。 */}
      {!guideHidden && (
        <div className="oa-guide" data-testid="catalog-guide" style={{ marginBottom: 0 }}>
          <b>使い方</b>
          <span>①商品をカートに入れる → ②レジで送付先を選ぶ → ③発注を確定</span>
          <button
            type="button"
            onClick={hideGuide}
            className="oa-guide-x kz-pressable"
            data-testid="catalog-guide-close"
            aria-label="使い方を閉じる"
            style={{ ...BTN_RESET, background: "none", border: "none" }}
          >
            <CloseIcon />
          </button>
        </div>
      )}

      {/* 検索（Design `.oa-search`）＝虫めがねを箱の中に入れる。**入力は 16px 未満にしない**
          （iOS が入力時にページを拡大して戻らない・05 §5.3）＝寸法は liff.css が持つ。 */}
      <div className="oa-search" style={{ marginBottom: 0 }}>
        <SearchIcon />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="catalog-search"
          placeholder="商品名・品番で検索"
          aria-label="商品を探す"
          autoComplete="off"
        />
      </div>

      {/* 分類チップ（D-95④）＝**マスタの分類列**から自動で出る。卸の絞り込みではない。 */}
      {showChips && (
        <div className="kz-chiprow" role="group" aria-label="分類でしぼりこむ">
          <Chip
            on={activeCategory === null}
            data-testid="chip-cat-all"
            onClick={() => setCategory(null)}
            style={CHIP_STYLE}
          >
            すべて
          </Chip>
          {categories.map((c) => (
            <Chip
              key={c}
              on={activeCategory === c}
              data-testid={`chip-cat-${c}`}
              onClick={() => setCategory(activeCategory === c ? null : c)}
              style={CHIP_STYLE}
            >
              {c}
            </Chip>
          ))}
        </div>
      )}

      {entries.length === 0 ? (
        <Empty
          title="商品がまだ登録されていません"
          hint={`${usable.map((c) => c.tenant_name).join("・")} にお問い合わせください。`}
        />
      ) : visible.length === 0 ? (
        <Empty title="該当する商品がありません" hint="別の言葉でお探しください。" />
      ) : (
        <ProductList
          entries={visible}
          withHeadings={withHeadings}
          showVendorName={showVendorName}
          ambiguous={ambiguous}
          // 「他N社」は PC のカードだけ（スマホのカードは幅が無く、卸名だけで一杯になる）
          altVendors={isDesktop ? altVendors : null}
          onOpen={(e) => setSheetKey(e.key)}
        />
      )}

      {/* 税の扱い（D-90⑩・2026-08-09 森井氏確認④）＝価格は税抜であることをこの画面で言う。
          ★2文目（M1.9b・Design `.oa-foot`）＝**ここに無い商品がある理由**を先に言う
          （「あるはずの商品が出ない」は接続していない卸の商品を探しているだけのことが多い）。 */}
      <div className="oa-foot">
        {`価格は、お客様と各お取引先（卸売業者）との間のお取引単価（税抜）です。ご接続中の${usable.length}社の商品だけが表示されます。`}
      </div>
    </div>
  );

  return (
    <>
      <div className="kz-catalog-layout">
        {main}
        {/* PC＝右固定カート（D-98⑤）。モバイルはヘッダーのカートボタンが入口（描画しない＝
            レール用の配送料・便クエリをモバイルで発火させない）。 */}
        {isDesktop && <CartRail client={client} connections={connections} />}
      </div>

      {sheetEntry && (
        <ProductSheet
          // ★key＝表示対象が替わったら**数量を初期値へ戻す**（前の商品の 60枚 を引き継がない）
          key={sheetEntry.key}
          entry={sheetEntry}
          entries={entries}
          binsByTenant={binsByTenant}
          showVendorName={showVendorName}
          onSwitch={(e) => setSheetKey(e.key)}
          onClose={() => setSheetKey(null)}
          onAdded={(name) => {
            setSheetKey(null);
            setToast(`カートに追加しました（${name}）`);
          }}
        />
      )}

      {/* トースト（M1.9b・Design `.oa-toasth`/`.oa-toast`）＝先頭のチェック印が「効いた」の合図。 */}
      {toast && (
        <div className="oa-toasth">
          <div className="oa-toast" data-testid="catalog-toast" role="status" aria-live="polite">
            <CheckIcon size={14} />
            <span>{toast}</span>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * チップのタップ領域（05 §5.3＝現場で手袋のまま押す）。
 * ★**寸法だけ**をここで持つ＝色・文字・左右の詰めは liff.css の `.kz-chip` 上書き（Design 値）に任せる。
 *   ここで padding まで書くと inline が勝って Design 側の詰めが効かない。
 */
const CHIP_STYLE = {
  minHeight: 44,
  display: "inline-flex",
  alignItems: "center",
} as const;

/**
 * 読み込み中のカタログ（M1.7 → M1.9 で2列カードの形に追随）。
 * 素の div の帯だけで描く＝testid も商品も置かない。
 */
function CatalogSkeleton() {
  return (
    <div aria-hidden="true" style={{ display: "grid", gap: "var(--kz-space-3)" }}>
      <Skeleton rows={1} height={44} />
      <div style={{ display: "flex", gap: "var(--kz-space-2)" }}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="kz-skeleton" style={{ height: 44, width: 84, borderRadius: 999 }} />
        ))}
      </div>
      <div className="kz-catalog-grid">
        {[0, 1, 2, 3].map((i) => (
          <Card key={i} padding="var(--kz-space-3)">
            <div className="kz-skeleton" style={{ height: 92, marginBottom: "var(--kz-space-3)" }} />
            <Skeleton rows={3} height={13} />
          </Card>
        ))}
      </div>
    </div>
  );
}

/**
 * 商品カードの一覧。**分類の見出しは付けるが testid は付けない**（M1.8 の判断を維持）。
 */
function ProductList({
  entries,
  withHeadings,
  showVendorName,
  ambiguous,
  altVendors,
  onOpen,
}: {
  entries: Entry[];
  withHeadings: boolean;
  showVendorName: boolean;
  ambiguous: Set<string>;
  /** 品名＋規格 → 扱っている卸の集合。null＝「他N社」を出さない（スマホ）。 */
  altVendors: Map<string, Set<number>> | null;
  onOpen: (e: Entry) => void;
}) {
  const card = (e: Entry) => (
    <ProductCard
      key={e.key}
      entry={e}
      showVendorName={showVendorName}
      showNumber={ambiguous.has(dupKey(e)) && e.product.display_no !== null}
      altCount={altVendors === null ? 0 : (altVendors.get(sameProductKey(e.product))?.size ?? 1) - 1}
      onOpen={() => onOpen(e)}
    />
  );

  if (!withHeadings) return <div className="kz-catalog-grid">{entries.map(card)}</div>;

  const blocks: { category: string; items: Entry[] }[] = [];
  for (const e of entries) {
    const last = blocks[blocks.length - 1];
    if (last && last.category === e.category) last.items.push(e);
    else blocks.push({ category: e.category, items: [e] });
  }

  return (
    <>
      {blocks.map((b) => (
        <section key={b.category}>
          <h2
            style={{
              margin: "var(--kz-space-2) 0 var(--kz-space-2)",
              fontSize: "var(--kz-font-size-sm)",
              fontWeight: 700,
              color: "var(--kz-color-ink-muted)",
            }}
          >
            {b.category}
          </h2>
          <div className="kz-catalog-grid">{b.items.map(card)}</div>
        </section>
      ))}
    </>
  );
}

/**
 * 商品写真（FR-119・D-64）。未登録はプレースホルダ＝並びが崩れない。
 * 高さ・地・角丸は**クラス側**が持つ（カード＝`.pcard-illus` 92px／シート＝`.pd-illus` 130px）。
 */
function ProductPhoto({
  product,
  className,
  testId,
}: {
  product: PartnerProduct;
  className: string;
  testId?: string;
}) {
  return (
    <span className={className} data-testid={testId}>
      {product.image_url ? (
        <img
          src={product.image_url}
          alt=""
          loading="lazy"
          // ★高さは `stretch` で枠に合わせる＝この枠は `place-items: center` なので、
          //   `height: 100%` と書いても**枠の高さが不定と見なされて `auto` に落ち**、写真は
          //   自分の縦横比のまま置かれる（実測：92px の枠に 153px が入り、下だけ切れて
          //   真ん中が写らない）。`objectFit: cover` が効くのは枠に合った後。
          style={{ width: "100%", height: "auto", minHeight: 0, objectFit: "cover", alignSelf: "stretch" }}
        />
      ) : (
        <span style={{ fontSize: 10.5, color: "var(--kz-color-ink-faint)" }}>写真なし</span>
      )}
    </span>
  );
}

/**
 * 商品1枚のカード（M1.9・D-99）＝**タップで商品詳細シートを開く**。**値段が主役**。
 *
 * `price-{id}` / `photo-{id}` / `product-vendor-{id}` は**据え置き**（検証の指し先は同じ）。
 * `add-{id}` はシートの中へ移った（意味＝「この商品をカートに入れた」は不変）。
 */
function ProductCard({
  entry,
  showVendorName,
  showNumber,
  altCount,
  onOpen,
}: {
  entry: Entry;
  showVendorName: boolean;
  showNumber: boolean;
  /** 同じ品名・規格を扱う**ほかの卸**の数（0＝出さない）。 */
  altCount: number;
  onOpen: () => void;
}) {
  const { product, tenant } = entry;

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`open-${product.product_id}`}
      aria-label={`${product.name} の詳細を開く`}
      className="pcard kz-pressable"
      style={BTN_RESET}
    >
      <ProductPhoto product={product} className="pcard-illus" testId={`photo-${product.product_id}`} />

      {/* ★2行で切る＝品名の長さでカードの高さが揃わなくなると、価格の位置が行ごとにずれて
          「並べて比べる」ができない（`.kz-clamp-2` は E2E が品名を読む足がかりでもある）。 */}
      <span className="pcard-name kz-clamp-2">{product.name}</span>

      {/* 規格。**ロット（◯◯から・◯◯単位）はここに出さない**（M1.9b＝一覧は「選ぶ」に専念し、
          買い方の条件は決める場所＝詳細シートで言う）。商品番号は**見分けが要るときだけ**。 */}
      {(product.spec || showNumber) && (
        <span className="pcard-spec">
          {product.spec}
          {product.spec && showNumber && " ／ "}
          {showNumber && <span className="kz-num">{product.display_no}</span>}
        </span>
      )}

      {/* ★どの卸の商品か（FR-126・D-95）＝2社以上接続しているときだけ。
          M1.9b で**ピルから素のテキストへ**＝カードの主役は写真・品名・価格で、卸名は添え物。 */}
      {showVendorName && (
        <span className="pcard-sup" data-testid={`product-vendor-${product.product_id}`} style={ELLIPSIS}>
          {tenant.tenant_name}
          {/* 「他N社」＝同じ品名をほかの卸も扱っている合図（PC のみ）。押すと開く詳細シートの
              「同じ品名のお取扱い（参考）」で値段を並べて比べられる。 */}
          {altCount > 0 && <span className="pcard-alt">{`他${altCount}社`}</span>}
        </span>
      )}

      {/* ★**価格が主役**（D-95）＝取引単価（単価3層の解決結果・04 §5）。未確定は「—」。
          Design の並びでは価格が最下段＝視線の着地点。 */}
      <span className="pcard-price" data-testid={`price-${product.product_id}`}>
        <b>
          <Money value={product.unit_price} />
        </b>
        {product.unit_price !== null && <small>{`/${product.unit}`}</small>}
      </span>
    </button>
  );
}

/**
 * 商品詳細シート（M1.9・D-99）＝数量を決めてカートに入れる場所。
 *
 * ★「同じ品名のほかのお取扱い（参考）」＝**品名＋規格の正規化一致**で他卸の商品を並べる。
 *   名寄せ（同一視）はしない＝タップすると**表示対象がその商品に切り替わる**だけ（FR-126）。
 */
function ProductSheet({
  entry,
  entries,
  binsByTenant,
  showVendorName,
  onSwitch,
  onClose,
  onAdded,
}: {
  entry: Entry;
  entries: Entry[];
  binsByTenant: Map<number, PartnerBin[]>;
  showVendorName: boolean;
  onSwitch: (e: Entry) => void;
  onClose: () => void;
  onAdded: (name: string) => void;
}) {
  const { product, tenant } = entry;
  const cart = useCart();
  const [qty, setQty] = useState(product.qty_start);
  const bins = binsByTenant.get(tenant.tenant_id);
  // 内訳（D-101）は**掛率で解けた商品だけ**入る。`??` は「返さない版の API」対策（下の注記）。
  const listPrice = product.list_price ?? null;
  const kakePercent = product.kake_percent ?? null;

  /**
   * 同じ品名・規格のお取扱い＝**いま見ているものも含めた**候補（M1.9b・Design `.sup-opt`）。
   * ★並びは `entries`（分類→品名→卸の接続順）のまま＝**選び替えても行が動かない**。
   * ★同じ卸の中の同名品は候補にしない（別ロット等＝「ほかの卸の取扱い」ではない）。
   */
  const candidates = entries.filter(
    (e) =>
      sameProductKey(e.product) === sameProductKey(product) &&
      (e.key === entry.key || e.tenant.tenant_id !== tenant.tenant_id),
  );

  // 最安の印＝**値段に差があるときだけ**（全部同じ値段のとき全行に「最安」を付けても意味が無い）。
  const prices = candidates
    .map((c) => c.product.unit_price)
    .filter((v): v is string => v !== null)
    .map(Number);
  const cheapest = prices.length > 0 ? Math.min(...prices) : null;
  const hasSpread = prices.length >= 2 && Math.max(...prices) > (cheapest ?? 0);

  // 最短お届け＝今日の締めに間に合う便があればその最短日、無ければ全便の最短日。
  const soonest =
    bins && bins.length > 0
      ? (bins.find((b) => b.available_today) ??
        bins.reduce((a, b) => (b.earliest_date < a.earliest_date ? b : a), bins[0]))
      : null;

  function add() {
    cart.add(tenant.tenant_id, {
      product_id: product.product_id,
      name: product.name,
      spec: product.spec,
      unit: product.unit,
      unit_price: product.unit_price,
      qty,
    });
    onAdded(product.name);
  }

  const subtotal = product.unit_price === null ? null : String(Number(product.unit_price) * qty);

  return (
    <Sheet label={product.name} onClose={onClose} testId="product-sheet">
      <ProductPhoto product={product} className="pd-illus" />

      <h2 className="pd-name" style={{ margin: 0, lineHeight: 1.4, paddingRight: 40 }}>
        {product.name}
        {product.display_no && <span className="pd-code kz-num">{product.display_no}</span>}
      </h2>
      {/* 規格とロット。**ロットはここに出す**＝数量を決めるのはこの画面なので、
          「何枚から・何枚単位で買えるか」が要るのもこの画面（M1.9b でカードからは外した）。 */}
      <p className="pd-spec">
        {product.spec && `${product.spec} ／ `}
        {lotText(product)}
      </p>

      {/* ★同じ品名のお取扱い（D-99③）＝**いま見ているものを含めて**並べ、選び替えられる。
          「同じ商品」とは断定しない（名寄せしない＝卸が違えば別商品・FR-126）ので「参考」。
          1社としか接続していない職人には出さない（選びようが無い＝押せない行を並べない）。 */}
      {showVendorName && (
        <>
          <div className="pd-lbl">同じ品名のお取扱い（参考）</div>
          <div className="pd-sups" role="group" aria-label="同じ品名のお取扱い">
            {candidates.map((c) => {
              const self = c.key === entry.key;
              const price = c.product.unit_price;
              const summary = binSummary(binsByTenant.get(c.tenant.tenant_id));
              const min = hasSpread && price !== null && Number(price) === cheapest;
              const body = (
                <>
                  <span className="so-chk">{self && <CheckIcon />}</span>
                  <span className="so-n">
                    <b style={ELLIPSIS}>{c.tenant.tenant_name}</b>
                    {summary && <small style={ELLIPSIS}>{summary}</small>}
                  </span>
                  <span className="so-p">
                    <span>
                      <Money value={price} />
                      {price !== null && <small>{`/${c.product.unit}`}</small>}
                    </span>
                    {min && <em className="so-min">最安</em>}
                  </span>
                </>
              );
              // 選択中の行は**押せる見た目にしない**＝押しても何も起きないボタンを置かない。
              return self ? (
                <div key={c.key} className="sup-opt on" data-testid="sheet-self" aria-current="true">
                  {body}
                </div>
              ) : (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => onSwitch(c)}
                  data-testid={`sheet-alt-${c.tenant.tenant_id}-${c.product.product_id}`}
                  className="sup-opt kz-pressable"
                  style={BTN_RESET}
                >
                  {body}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* 単価の内訳（M1.9b・D-101）＝**掛率で解けた商品だけ**「定価 → 掛け率 → 取引単価」の3行。
          個別単価・定価そのままの商品は `list_price` が null で返る＝取引単価の1行だけになる
          （「定価と同じ」と並べると値引きが無いことを強調してしまう）。
          ★`??` で受けるのは、**このフィールドを返さない版の API**（D-101 前）に当たったとき
          `undefined` が来て `!== null` を素通りし、「小売価格（定価）—／掛け率 undefined%」と
          出てしまうため（実測で踏んだ）。型の上では起きないが、画面は先に壊れる。 */}
      <div className="pd-price">
        {listPrice !== null && kakePercent !== null && (
          <>
            <div className="pdp-row">
              <span>小売価格（定価）</span>
              <s data-testid="sheet-list-price">
                <Money value={listPrice} />
                {`/${product.unit}`}
              </s>
            </div>
            <div className="pdp-row">
              <span>掛け率</span>
              <b className="pdp-kake kz-num" data-testid="sheet-kake">{`${kakePercent}%`}</b>
            </div>
          </>
        )}
        <div className="pdp-row main">
          <span>お取引単価（税抜）</span>
          <b data-testid="sheet-price">
            <Money value={product.unit_price} />
            {product.unit_price !== null && <small>{`/${product.unit}`}</small>}
          </b>
        </div>
      </div>

      {/* 最短お届け（bins API）＝「いま頼むといつ届くか」。締めは卸ごと・便ごと（06 §6.2）。
          ★在庫は出さない（liff.css の `.pd-avail` は DESIGN-HOLD＝在庫データを持っていない）。 */}
      {soonest && (
        <p
          style={{
            margin: "var(--kz-space-2) 0 0",
            fontSize: "var(--kz-font-size-sm)",
            color: "var(--kz-color-ink-muted)",
          }}
        >
          最短 <strong style={{ color: "var(--kz-color-ink)" }}>{formatDate(soonest.earliest_date)}</strong>
          {`（${soonest.name}`}
          {soonest.available_today ? `・本日 ${hhmm(soonest.cutoff_time)} まで受付）` : `・本日の受付は終了）`}
        </p>
      )}

      {/* 数量 → 小計 → カートに追加（この画面の最終アクション＝黄 CTA・D-98④） */}
      <div className="pd-buy">
        <Stepper value={qty} step={product.qty_step} unit={product.unit} onChange={setQty} label={product.name} />
        <span className="pd-sub">
          <small>小計（税抜）</small>
          <b data-testid="sheet-subtotal">
            <Money value={subtotal} />
          </b>
        </span>
      </div>

      <button
        type="button"
        onClick={add}
        data-testid={`add-${product.product_id}`}
        className="kz-btn kz-btn--accent kz-pressable"
        style={{ width: "100%", marginTop: "var(--kz-space-4)" }}
      >
        カートに追加
      </button>
    </Sheet>
  );
}
